"use client";

import { useState } from "react";
import { useCookieConsent, getDefaultConsent } from "@/contexts/CookieConsentContext";
import { CookiePreferences } from "@/components/CookiePreferences";
import { Modal, ModalContent } from "@/components/ui/Modal";
import { fsElement } from "@/lib/fs";

export function CookieConsentBanner() {
  const { consent, setConsent, ready } = useCookieConsent();
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Keep banner (and modal) visible while preferences dialog is open, so toggling a checkbox doesn't close it
  if (!ready || (consent !== null && !preferencesOpen)) return null;

  const handleAcceptAll = () => {
    setConsent({ necessary: true, analytics: true, marketing: true });
  };

  const handleRejectNonEssential = () => {
    setConsent(getDefaultConsent());
  };

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card shadow-lg"
        role="dialog"
        aria-label="Cookie consent"
      >
        <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            We use necessary cookies to run the site (e.g. login). You can optionally allow analytics and marketing cookies, or reject non-essential ones. See our{" "}
            <a href="/cookie-policy" className="text-primary hover:underline">
              Cookie policy
            </a>{" "}
            for details.
          </p>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleAcceptAll}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              {...fsElement("Accept All Cookies Button", { id: "accept-all-cookies", section: "cookie-banner" })}
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={handleRejectNonEssential}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
              {...fsElement("Reject Non-Essential Cookies Button", { id: "reject-cookies", section: "cookie-banner" })}
            >
              Reject non-essential
            </button>
            <button
              type="button"
              onClick={() => setPreferencesOpen(true)}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
              {...fsElement("Manage Cookie Preferences Button", { id: "manage-cookies", section: "cookie-banner" })}
            >
              Manage preferences
            </button>
          </div>
        </div>
      </div>

      <Modal isOpen={preferencesOpen} onClose={() => setPreferencesOpen(false)}>
        <ModalContent className="w-full max-w-lg rounded-lg border bg-card p-5 shadow-lg max-h-[90vh] overflow-y-auto">
          <h2 className="text-lg font-semibold text-foreground mb-2">Cookie preferences</h2>
          <CookiePreferences compact />
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => setPreferencesOpen(false)}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Save and close
            </button>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
}
